import React, { useLayoutEffect, useState } from 'react';
import { Group } from '@visx/group';
import { EngineeringOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const Workers = ({ data }) => {
  const theme = useTheme();
  const { t } = useTranslation('dashboard');
  const [dimensions, setDimensions] = React.useState({ width: 300, height: 300 });
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });

  useLayoutEffect(() => {
    const handleResize = () => {
      const container = document.getElementById('heatmap-container');
      if (container) {
        setDimensions({
          width: container.offsetWidth,
          height: container.offsetHeight,
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const container = document.getElementById('heatmap-container');
    let resizeObserver;
    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserver.observe(container);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver && container) {
        resizeObserver.unobserve(container);
        resizeObserver.disconnect();
      }
    };
  }, []);

  const containerWidth = dimensions.width;
  const containerHeight = dimensions.height;
  const rows = Math.floor((containerWidth - 20) / 18);
  const binSize = Math.max(15, Math.min((containerWidth - 20 - (rows - 1) * 3) / rows, (containerHeight - (Math.ceil(data.length / rows) - 1) * 3)));

  const getStatusColor = (status) => {
    if (status === 'online') {
      return theme.palette.contrast.green; // Зеленый для online
    } else if (status === 'offline') {
      return theme.palette.contrast.red; // Красный для offline
    } else if (status === null || status === undefined) {
      return theme.palette.contrast.gray; // Серый для null/undefined
    } else {
      return theme.palette.contrast.blue || '#2196f3'; // Синий для других статусов
    }
  };

  const handleMouseEnter = (event, item) => {
    const rect = event.target.getBoundingClientRect();
    const statusText = item.status === 'online' ? 'Online' :
                       item.status === 'offline' ? 'Offline' :
                       item.status === null ? t('infoUnknown') :
                       item.status.charAt(0).toUpperCase() + item.status.slice(1);
    setTooltip({
      visible: true,
      x: rect.x + rect.width / 2,
      y: rect.y - 10,
      content: `${t('infoModel')}: ${item.model}\nMAC: ${item.mac}\n${t('columnStatus')}: ${statusText}\n${t('logsTime')}: ${item.created_at}`,
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, content: '' });
  };

  return (
    <div id="heatmap-container" style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>
      <div style={{ position: 'absolute', top: 5, left: 5, display: 'flex', alignItems: 'center', fontSize: '18px', fontWeight: 700, fontFamily: "'Stem','Suisse Intl',sans-serif", color: theme.palette.text.primary }}>
        <EngineeringOutlined style={{ marginRight: '5px', color: theme.palette.text.primary }} />
        {t('workersTitle')}
      </div>
      <div style={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '14px',
        padding: '12px',
        marginTop: '35px',
        border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(120,110,180,0.14)',
        boxShadow: '0 8px 24px -12px rgba(23,15,90,0.18)',
        height: `${containerHeight - 55}px`,
      }}>
        <div style={{ textAlign: 'center', fontSize: '16px', marginBottom: '10px', color: theme.palette.text.secondary, fontWeight: 400 }}>
          {data.length} {t('workersDevice')}
        </div>
        <svg style={{ maxWidth: '100%', maxHeight: '100%' }} width={containerWidth} height={containerHeight - 55}>
          <Group top={0} left={0}>
            {data.map((item, index) => {
              const x = (index % rows) * (binSize + 3);
              const y = Math.floor(index / rows) * (binSize + 3);
              const color = getStatusColor(item.status);
              return (
                <rect
                  key={`rect-${index}`}
                  x={x}
                  y={y}
                  width={binSize}
                  height={binSize}
                  fill={color}
                  rx={5}
                  ry={5}
                  onMouseEnter={(event) => handleMouseEnter(event, item)}
                  onMouseLeave={handleMouseLeave}
                />
              );
            })}
          </Group>
        </svg>
        {tooltip.visible && (
          <div
            style={{
              position: 'absolute',
              top: tooltip.y,
              left: tooltip.x,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: '#fff',
              padding: '5px 10px',
              borderRadius: '5px',
              pointerEvents: 'none',
              whiteSpace: 'pre-line',
            }}
          >
            {tooltip.content}
          </div>
        )}
      </div>
    </div>
  );
};

export default Workers;
